<script setup>
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import { STATUS_COLORS, formatDate, formatMoney } from './helpers';

// One Pixta order in the sidebar card: what the buyer paid, its tickets, whether it can still be cancelled under the
// buyer's own rules (until when, how much, back where) and the refund, with resend and cancel for agents
const props = defineProps({
  order: { type: Object, required: true },
  // Pixta's admin (…/admin), for links to the order and its event
  adminUrl: { type: String, required: true },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['resend', 'cancel']);

const { t, locale } = useLocoI18n();
const isOpen = ref(false);
const cancelDialog = ref(null);

const lang = computed(() => intlLocale(locale.value));
const money = value => formatMoney(value, lang.value);
const date = value => formatDate(value, lang.value);

const cancel = computed(() => props.order.cancel || {});
const refundDestination = computed(() =>
  t(`LOCO.PIXTA.REFUND_TO.${cancel.value.refund_to}`)
);
const canResend = computed(
  () => props.order.status === 'paid' && props.order.tickets.length > 0
);

const confirmCancel = () => {
  cancelDialog.value.close();
  emit('cancel', props.order);
};
</script>

<template>
  <div class="flex flex-col gap-2 py-2">
    <button
      type="button"
      class="flex items-start justify-between gap-2 text-start"
      @click="isOpen = !isOpen"
    >
      <span class="flex flex-col min-w-0">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ order.event?.name }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ date(order.event?.starts_at) }} · {{ money(order.amount) }} ·
          {{ t(`LOCO.PIXTA.METHODS.${order.payment_method}`) }}
        </span>
      </span>
      <Label
        compact
        :label="t(`LOCO.PIXTA.STATUS.${order.status}`)"
        :color="STATUS_COLORS[order.status] || 'slate'"
      />
    </button>

    <div v-if="isOpen" class="flex flex-col gap-2 text-xs text-n-slate-11">
      <span class="flex flex-wrap gap-3">
        <a
          :href="`${adminUrl}/orders/${order.id}`"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 text-n-blue-11 hover:underline"
        >
          <span class="i-lucide-external-link size-3" aria-hidden="true" />
          {{ t('LOCO.PIXTA.ORDER.ADMIN') }}
        </a>
        <a
          v-if="order.event"
          :href="`${adminUrl}/events/${order.event.slug}`"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 text-n-blue-11 hover:underline"
        >
          <span class="i-lucide-external-link size-3" aria-hidden="true" />
          {{ t('LOCO.PIXTA.ORDER.EVENT_ADMIN') }}
        </a>
      </span>
      <span>
        {{
          t('LOCO.PIXTA.ORDER.BOUGHT', {
            date: date(order.paid_at || order.created_at),
          })
        }}
        <template v-if="Number(order.service_fee)">
          · {{ t('LOCO.PIXTA.ORDER.FEE', { fee: money(order.service_fee) }) }}
        </template>
        <template v-if="order.resale">
          · {{ t('LOCO.PIXTA.ORDER.RESALE') }}</template
        >
      </span>

      <ul v-if="order.tickets.length" class="flex flex-col gap-0.5">
        <li v-for="ticket in order.tickets" :key="ticket.id" class="flex gap-1">
          <span class="truncate text-n-slate-12">
            {{ ticket.holder || ticket.product }}
          </span>
          <span v-if="ticket.status === 'cancelled'" class="text-n-ruby-11">
            {{ t('LOCO.PIXTA.TICKET.CANCELLED') }}
          </span>
          <span v-else-if="ticket.checked_in_at">
            {{
              t('LOCO.PIXTA.TICKET.CHECKED_IN', {
                date: date(ticket.checked_in_at),
              })
            }}
          </span>
          <span v-else-if="ticket.transferred">
            {{ t('LOCO.PIXTA.TICKET.TRANSFERRED') }}
          </span>
        </li>
      </ul>

      <p v-if="cancel.allowed" class="m-0 text-xs text-n-teal-11">
        {{
          t('LOCO.PIXTA.CANCEL.ALLOWED', {
            date: date(cancel.deadline),
            amount: money(order.refundable_amount),
            destination: refundDestination,
          })
        }}
      </p>
      <p v-else-if="order.status === 'paid'" class="m-0 text-xs">
        {{ t(`LOCO.PIXTA.CANCEL.BLOCKED.${cancel.blocked_by}`) }}
      </p>
      <p v-if="order.refund" class="m-0 text-xs">
        {{
          t('LOCO.PIXTA.REFUND', {
            status: t(`LOCO.PIXTA.REFUND_STATUS.${order.refund.status}`),
            amount: money(order.refund.amount),
          })
        }}
      </p>

      <div v-if="canResend || cancel.allowed" class="flex flex-wrap gap-2">
        <Button
          v-if="canResend"
          type="button"
          xs
          faded
          slate
          icon="i-lucide-mail"
          :label="t('LOCO.PIXTA.RESEND')"
          :disabled="busy"
          @click="emit('resend', order)"
        />
        <Button
          v-if="cancel.allowed"
          type="button"
          xs
          faded
          ruby
          icon="i-lucide-ban"
          :label="t('LOCO.PIXTA.CANCEL.ACTION')"
          :disabled="busy"
          @click="cancelDialog.open()"
        />
      </div>
    </div>

    <Dialog
      ref="cancelDialog"
      type="alert"
      :title="
        t('LOCO.PIXTA.CANCEL.CONFIRM_TITLE', { event: order.event?.name })
      "
      :description="
        t('LOCO.PIXTA.CANCEL.CONFIRM', {
          amount: money(order.refundable_amount),
          destination: refundDestination,
        })
      "
      :confirm-button-label="t('LOCO.PIXTA.CANCEL.ACTION')"
      :cancel-button-label="t('LOCO.PIXTA.CANCEL.KEEP')"
      @confirm="confirmCancel"
    />
  </div>
</template>
